import React from 'react';
import PropTypes from 'prop-types';
import withAccountFormStepStyles from 'Components/UI/Accounts/AccountApplication/Step/withAccountFormStepStyles';
import MetaField from 'Components/UI/Meta/MetaForm/Components/MetaControlField';
import useMetaLayoutContext from 'Components/UI/Meta/MetaForm/Components/MetaLayoutContext';
import Typography from 'Components/UI/Typography';
import { IconButton } from '@material-ui/core';
import { Collapse } from '@mui/material';
import BeneficiaryModal from 'Components/UI/Accounts/AccountApplication/Step/BeneficiaryInformation/BeneficiaryModal';
import { AddCircleRounded } from '@material-ui/icons';
import Button from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/Button';
import { displayErrorMessage } from 'Helpers/FacetNotificationManager';
import { withStyles } from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/styles';
import { animated, config, useTransition } from 'react-spring';
import List from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/List';
import ListItem from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/ListItem';
import ListItemText from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/ListItemText';
import EditIcon from '@material-ui/icons/Edit';
import ListItemIcon from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/ListItemIcon';

const AddButton = withStyles({
  root: {
    marginLeft: 'auto',
  },
})(Button);

const formatPrimaryText = resource => {
  const isOrganization = resource.attr('isOrganization') === true || resource.attr('isOrganization') === 'true';
  return isOrganization ? `Organization/Trust/Charity: ${resource.attr('lastName')}` : `${resource.attr('firstName')} ${resource.attr('lastName')}`;
};

const BeneficiaryList = withStyles(theme => ({
  root: {},
  item: {
    '&:hover': {
      cursor: 'pointer',
      backgroundColor: theme.palette.facet.lightGray,
    },
  },
}))(({
  classes, disabled, beneficiaries, onEdit,
}) => {
  const transitions = useTransition(beneficiaries?.resources().list(), {
    from: { opacity: 0 },
    enter: { opacity: 1 },
    keys: beneficiaries?.resources().list().map(b => b.id()),
    config: config.molasses,
  });

  return (
    <List test="account-beneficiary-list">
      {transitions(({ opacity }, a) => {
        return (
          <animated.div
            style={{
              opacity: opacity.to({ range: [0.0, 1.0], output: [0, 1] }),
            }}
          >
            <ListItem button disabled={disabled} className={classes.item} onClick={() => onEdit(a)}>
              <ListItemText
                primary={formatPrimaryText(a)}
                secondary={`${a.selectedOptionPrompt('beneficiaryType')} - ${a.attr('sharePercentage')}%`}
              />
              <ListItemIcon>
                <IconButton
                  edge="end"
                  aria-label="delete"
                  color="primary"
                  disabled={disabled}
                >
                  <EditIcon />
                </IconButton>
              </ListItemIcon>
            </ListItem>
          </animated.div>
        );
      })}
    </List>
  );
});

BeneficiaryList.propTypes = {
  classes: PropTypes.object.isRequired,
  beneficiaries: PropTypes.object.isRequired,
};


function BeneficaryInformation({ classes }) {
  const { resource, handleChange } = useMetaLayoutContext();
  const [selectedBeneficiary, setSelectedBeneficiary] = React.useState(null);

  const hasBeneficiaries = resource.attr('hasBeneficiary') === 'true';
  const beneficiaries = resource.attr('beneficiaries');

  return (
    <div className={classes.root}>
      <div className={classes.row}>
        <MetaField
          attr="hasBeneficiary"
          onChange={(v) => {
            if (v === 'true' && beneficiaries?.resources().length() === 0) {
              setSelectedBeneficiary(beneficiaries.create());
            }
          }}
        />
      </div>
      <Collapse in={hasBeneficiaries && beneficiaries}>
        <div className={classes.collapseRoot}>
          <div className={classes.row}>
            <Typography
              className={classes.header}
              variant="h3"
              color="primary"
              component={'h2'}
            >
              {'Beneficiaries'}
            </Typography>
            <AddButton
              test={'add-beneficiary'}
              className={classes.addButton}
              startIcon={<AddCircleRounded color="secondary" />}
              onClick={() => {
                setSelectedBeneficiary(beneficiaries.create());
              }}
            >
              {'Add Beneficiary'}
            </AddButton>
          </div>
          {beneficiaries?.resources().length() > 0 && (
          <BeneficiaryList
            beneficiaries={beneficiaries}
            onEdit={b => setSelectedBeneficiary(b)}
          />
          )}
        </div>
      </Collapse>
      {selectedBeneficiary && (
        <BeneficiaryModal
          beneficary={selectedBeneficiary}
          setBeneficiary={setSelectedBeneficiary}
          onSubmit={async () => {
            try {
              const next = await beneficiaries.refresh();
              handleChange('beneficiaries', next);
              setSelectedBeneficiary(null);
            } catch (e) {
              displayErrorMessage(e, 'Failed to load beneficiaries.');
            }
          }}
          onDelete={async () => {
            try {
              const next = await beneficiaries.refresh();
              handleChange('beneficiaries', next);
              setSelectedBeneficiary(null);
            } catch (e) {
              displayErrorMessage(e, 'Failed to load beneficiaries.');
            }
          }}
          onCancel={() => setSelectedBeneficiary(null)}
        />
      )}
    </div>
  );
}

BeneficaryInformation.propTypes = {
  classes: PropTypes.object.isRequired,
};

export default withAccountFormStepStyles(BeneficaryInformation);
